import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useEmulationGuard } from "@/hooks/useEmulationGuard";
import { supabase } from "@/lib/supabase";
import { captureError } from "@/lib/sentry";
import { Mail, AlertCircle, RefreshCcw, Loader2, Play } from "lucide-react";
import type { EmailProvider } from "@/lib/email/types";
import { outlookService } from "@/lib/email/outlook";
import { GMAIL_CONFIG } from "@/lib/email/config";
import { cn } from "@/lib/utils";
import { SenderSettingsDialog } from "./SenderSettingsDialog";
import { SenderDomainHealthPanel } from "./SenderDomainHealthPanel";
import { useSenderDomainHealth } from "@/hooks/useSenderDomainHealth";
import { SenderDisconnectConfirmDialog } from "./SenderDisconnectConfirmDialog";
import {
  bulkPauseCampaigns,
  checkSenderDisconnectImpact,
  type AffectedCampaign,
} from "./_senderDisconnectChecks";
import { emailBackendApi } from "@/lib/email/edge-functions-api";
import { EmailProviderCheck } from "./EmailProviderCheck";

// Per-provider active account_pause control_ids, keyed by email_providers.id.
// Used both to flag a provider as emergency-paused AND to know which control
// rows the Resume / Reconnect flows need to deactivate via
// safety-controls-admin.
type AccountPauseMap = Map<string, string[]>;

export function SenderSettings() {
  const [providers, setProviders] = useState<EmailProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState<EmailProvider | null>(null);
  const [needsReconnect, setNeedsReconnect] = useState<Set<string>>(new Set());
  // ID of the provider currently having its pre-flight impact checked (spinner
  // on its Disconnect button so the user knows the click registered).
  const [checkingProviderId, setCheckingProviderId] = useState<string | null>(null);
  // When non-null, the user has clicked Disconnect on a provider that has
  // affected campaigns — show the confirm dialog. NOTE: MSAL revocation for
  // Outlook must NOT happen until the user confirms (spec #5: cancelling
  // the dialog must leave the user logged into Microsoft).
  const [pendingDisconnect, setPendingDisconnect] = useState<{
    provider: EmailProvider;
    affectedCampaigns: AffectedCampaign[];
  } | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  // Providers whose linked hermes_email_accounts row has
  // emergency_pause_active=true OR has an active account_pause safety control.
  // Distinct from needsReconnect — a paused account may still have a valid
  // token (e.g. quota burst, bounce spike), so we surface a "Resume account"
  // button instead of forcing a re-OAuth.
  const [emergencyPaused, setEmergencyPaused] = useState<Set<string>>(new Set());
  // Per-provider active account_pause control_ids; Resume / Reconnect flows
  // deactivate these via safety-controls-admin before finishing / redirecting.
  const [accountPauseControls, setAccountPauseControls] = useState<AccountPauseMap>(
    new Map()
  );
  // Provider currently in the middle of a Resume click — shows a spinner on
  // its button.
  const [resumingProviderId, setResumingProviderId] = useState<string | null>(null);
  // "Not sure which?" helper result -- highlights the matching Connect card only.
  const [suggested, setSuggested] = useState<"google" | "microsoft" | null>(null);
  const { currentWorkspace } = useWorkspaces();
  // Domain authentication checks (M1, #752). Fetched once for the workspace --
  // the results are cached per DOMAIN and several senders usually share one.
  const {
    byDomain: domainHealth,
    loading: healthLoading,
    refreshing: healthRefreshing,
    error: healthError,
    recheck: recheckDomain,
  } = useSenderDomainHealth(currentWorkspace?.id);
  const { toast } = useToast();
  const { isEmulating, blockIfEmulating } = useEmulationGuard();
  const queryClient = useQueryClient();

  // Clear any stale MSAL interaction state on mount (fixes "interaction_in_progress" errors)
  useEffect(() => {
    outlookService.clearInteractionState().catch(console.warn);
  }, []);

  useEffect(() => {
    if (currentWorkspace) {
      fetchProviders();
    }
  }, [currentWorkspace?.id]);

  const fetchProviders = async () => {
    try {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        setProviders([]);
        setNeedsReconnect(new Set());
        setEmergencyPaused(new Set());
        setAccountPauseControls(new Map());
        return;
      }

      const { data, error } = await supabase
        .from('email_providers')
        .select('*')
        .eq('workspace_id', workspaceId);

      if (error) throw error;
      const providerRows: EmailProvider[] = data || [];
      setProviders(providerRows);

      // Expired-token Reconnect set (legacy behavior).
      const needsReconnectSet = new Set<string>();
      providerRows.forEach(provider => {
        if (provider.expires_at && new Date(provider.expires_at) <= new Date()) {
          needsReconnectSet.add(provider.id);
        }
        // Legacy SPA-flow Outlook accounts hold short-lived (24h) tokens —
        // flag them for reconnect to the 90-day Web flow. Scoped to outlook:
        // gmail rows also default auth_flow='spa' and must NOT be flagged.
        if (provider.type === 'outlook' && (provider as any).auth_flow === 'spa') {
          needsReconnectSet.add(provider.id);
        }
      });
      setNeedsReconnect(needsReconnectSet);

      // Join: email_providers → hermes_email_accounts (workspace_id + email)
      // → active hermes_system_safety_controls of control_type='account_pause'.
      // Used to surface emergency-paused providers and to enumerate the control
      // rows the Resume / Reconnect flows must deactivate via the edge fn.
      const emergencyPausedSet = new Set<string>();
      const pauseControlMap: AccountPauseMap = new Map();

      const providerEmails = providerRows.map(p => p.email);
      if (providerEmails.length > 0) {
        const { data: hermesAccounts, error: hermesErr } = await supabase
          .from('hermes_email_accounts')
          .select('id, email_address, emergency_pause_active')
          .eq('workspace_id', workspaceId)
          .in('email_address', providerEmails);

        if (hermesErr) {
          // Non-fatal — log + continue so the rest of the page still renders.
          console.error('Failed to fetch hermes_email_accounts:', hermesErr);
          captureError(hermesErr as unknown as Error, {
            feature: 'sender-settings-hermes-accounts',
          });
        } else if (hermesAccounts && hermesAccounts.length > 0) {
          // Build hermes account_id → email map for the next query.
          const accountIdByEmail = new Map<string, string>();
          const pausedHermesIds = new Set<string>();
          hermesAccounts.forEach(a => {
            accountIdByEmail.set(a.email_address, a.id);
            if (a.emergency_pause_active) pausedHermesIds.add(a.id);
          });

          // Fetch ALL active account_pause controls for these hermes accounts
          // (not just the paused-flag ones). The hermes_email_accounts flag
          // and the control rows can drift; trust the safety-controls table
          // for what Resume actually needs to deactivate.
          const hermesIds = Array.from(accountIdByEmail.values());
          const { data: pauseControls, error: ctrlErr } = await supabase
            .from('hermes_system_safety_controls')
            .select('id, target_id')
            .eq('control_type', 'account_pause')
            .eq('is_active', true)
            .in('target_id', hermesIds);

          if (ctrlErr) {
            console.error('Failed to fetch account_pause controls:', ctrlErr);
            captureError(ctrlErr as unknown as Error, {
              feature: 'sender-settings-pause-controls',
            });
          } else {
            // hermes_email_account.id → control_ids[]
            const controlsByHermesId = new Map<string, string[]>();
            (pauseControls || []).forEach(c => {
              const list = controlsByHermesId.get(c.target_id) || [];
              list.push(c.id);
              controlsByHermesId.set(c.target_id, list);
            });

            providerRows.forEach(p => {
              const hermesId = accountIdByEmail.get(p.email);
              if (!hermesId) return;
              const controls = controlsByHermesId.get(hermesId) || [];
              const isPaused =
                pausedHermesIds.has(hermesId) || controls.length > 0;
              if (isPaused) emergencyPausedSet.add(p.id);
              if (controls.length > 0) pauseControlMap.set(p.id, controls);
            });
          }
        }
      }

      setEmergencyPaused(emergencyPausedSet);
      setAccountPauseControls(pauseControlMap);
    } catch (error) {
      console.error('Error fetching providers:', error);
      captureError(error as Error, { feature: 'sender-settings-fetch' });
      toast({
        title: "Error",
        description: "Failed to load email providers",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Deactivate every active account_pause safety control for this provider.
  // Used both by the Resume path (clear-only) and the Reconnect path (clear,
  // then redirect to OAuth). Returns true if all deactivations succeeded or
  // there were none to do; false if at least one failed.
  const deactivateAccountPauseControls = async (
    providerId: string,
  ): Promise<boolean> => {
    const controlIds = accountPauseControls.get(providerId) || [];
    if (controlIds.length === 0) return true;
    let allOk = true;
    for (const id of controlIds) {
      const result = await emailBackendApi.deactivateSafetyControl(id);
      if (!result.success) {
        allOk = false;
        console.error(
          `Failed to deactivate account_pause control ${id}:`,
          result.error,
        );
        captureError(
          new Error(result.error || 'deactivate failed'),
          { feature: 'sender-resume', control_id: id },
        );
      }
    }
    return allOk;
  };

  const connectGmail = async (opts: { providerId?: string } = {}) => {
    if (blockIfEmulating('Connecting an email provider')) return;
    try {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      // If this Reconnect originated from an emergency-paused provider, lift
      // the account_pause controls BEFORE redirect. When the user returns
      // from Google OAuth, the account is already unblocked.
      // If the pre-deactivate fails, ABORT the redirect — otherwise the user
      // completes OAuth and lands on a still-paused account with no signal
      // anything went wrong.
      if (opts.providerId && emergencyPaused.has(opts.providerId)) {
        const ok = await deactivateAccountPauseControls(opts.providerId);
        if (!ok) {
          toast({
            title: "Couldn't lift the safety hold",
            description:
              "Try the Resume button before reconnecting, or contact ops.",
            variant: "destructive",
          });
          return;
        }
      }

      localStorage.setItem('connecting_workspace_id', currentWorkspace.id);
      localStorage.setItem('connecting_provider', 'gmail');

      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        new URLSearchParams({
          client_id: GMAIL_CONFIG.clientId,
          redirect_uri: GMAIL_CONFIG.redirectUri,
          response_type: 'code',
          scope: GMAIL_CONFIG.scope,
          access_type: 'offline',
          prompt: 'consent',
        }).toString();

      window.location.href = authUrl;
    } catch (error) {
      console.error('Error connecting Gmail:', error);
      captureError(error as Error, { feature: 'sender-settings-connect-gmail' });
      toast({
        title: "Error",
        description: "Failed to connect Gmail account",
        variant: "destructive",
      });
    }
  };

  const connectOutlook = async (
    { forceFresh = false, providerId }: { forceFresh?: boolean; providerId?: string } = {},
  ) => {
    if (blockIfEmulating('Connecting an email provider')) return;
    try {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      // If this Reconnect originated from an emergency-paused provider, lift
      // the account_pause controls BEFORE redirect. When the user returns
      // from Microsoft, the account is already unblocked. ABORT the redirect
      // if the pre-deactivate fails — otherwise the user completes OAuth and
      // lands on a still-paused account.
      if (providerId && emergencyPaused.has(providerId)) {
        const ok = await deactivateAccountPauseControls(providerId);
        if (!ok) {
          toast({
            title: "Couldn't lift the safety hold",
            description:
              "Try the Resume button before reconnecting, or contact ops.",
            variant: "destructive",
          });
          return;
        }
      }

      // Store workspace ID for the callback to use
      localStorage.setItem('connecting_workspace_id', currentWorkspace.id);
      localStorage.setItem('connecting_provider', 'outlook');

      // forceFresh=true is for the Reconnect path — forces Microsoft to
      // re-authenticate the user (prompt='login'), which mints fresh access +
      // refresh tokens but does NOT re-trigger the admin-consent workflow.
      // (PR #470 originally tried prompt='consent' here, but that re-triggers
      // admin consent in restricted tenants on every click; see outlook.ts.)
      await outlookService.login({ forceFresh });
      // Code below won't execute - page redirects to Microsoft
    } catch (error) {
      console.error('Error connecting Outlook:', error);
      captureError(error as Error, { feature: 'sender-settings-connect-outlook' });
      toast({
        title: "Error",
        description: typeof error === 'string' ? error : "Failed to connect Outlook account",
        variant: "destructive",
      });
    }
  };

  // Step 1 of the disconnect flow — run pre-flight impact check. If no
  // active/paused campaigns use this sender, disconnect immediately. Otherwise
  // open the confirm dialog (step 2). Critically, MSAL revocation for Outlook
  // is deferred until the user confirms — otherwise clicking Cancel would
  // still log them out of Microsoft.
  const handleDisconnectClick = async (provider: EmailProvider) => {
    if (blockIfEmulating('Disconnecting an email provider')) return;
    if (!currentWorkspace?.id) return;

    setCheckingProviderId(provider.id);
    try {
      const { affectedCampaigns } = await checkSenderDisconnectImpact({
        email: provider.email,
        workspaceId: currentWorkspace.id,
      });

      if (affectedCampaigns.length === 0) {
        // Safe path — no active/paused campaigns. Disconnect immediately,
        // preserving original behavior (MSAL revoke before DB delete is fine
        // here because there's no Cancel button to bail out of).
        await performDisconnect(provider, []);
        return;
      }

      // Affected campaigns exist — open confirm dialog. Don't touch MSAL yet.
      setPendingDisconnect({ provider, affectedCampaigns });
    } catch (error) {
      console.error('Error checking disconnect impact:', error);
      captureError(error as Error, { feature: 'sender-disconnect' });
      toast({
        title: "Error",
        description: "Failed to check campaigns for this sender. Try again.",
        variant: "destructive",
      });
    } finally {
      setCheckingProviderId(null);
    }
  };

  // Step 2 — actually perform the disconnect. Order on confirm (per spec #5):
  //   1. Bulk-pause affected campaigns (so in-flight sends stop cleanly)
  //   2. Delete the email_providers row (revokes Hermes' ability to send)
  //   3. Revoke MSAL session (Outlook only)
  // If MSAL revocation fails, the DB delete has already succeeded — we surface
  // the error but don't roll back: the user's intent was to disconnect.
  const performDisconnect = async (
    provider: EmailProvider,
    affectedCampaigns: AffectedCampaign[]
  ) => {
    setIsDisconnecting(true);
    try {
      // 1. Pause affected campaigns first, then invalidate the React Query
      // cache so other open campaign views see the paused state immediately
      // (otherwise they'd keep showing 'active' until refocus / manual refetch).
      if (affectedCampaigns.length > 0) {
        await bulkPauseCampaigns(affectedCampaigns.map((c) => c.id));
        queryClient.invalidateQueries({ queryKey: ['hermes-campaigns'] });
      }

      // 2. Delete the email_providers row.
      const { error: deleteError } = await supabase
        .from('email_providers')
        .delete()
        .eq('id', provider.id);

      if (deleteError) throw deleteError;

      // 3. Revoke MSAL session (Outlook only).
      if (provider.type === 'outlook') {
        await outlookService.disconnect();
      }

      toast({
        description: affectedCampaigns.length > 0
          ? `Disconnected ${provider.email} and paused ${affectedCampaigns.length} campaign${affectedCampaigns.length === 1 ? '' : 's'}.`
          : "Email provider disconnected successfully",
      });

      setPendingDisconnect(null);
      fetchProviders();
    } catch (error) {
      console.error('Error disconnecting provider:', error);
      captureError(error as Error, { feature: 'sender-disconnect' });
      toast({
        title: "Error",
        description: "Failed to disconnect email provider",
        variant: "destructive",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  // Resume path — provider's token is fine, only the safety controls + the
  // emergency_pause_active flag need lifting. No OAuth redirect. The
  // safety-controls-admin edge function clears
  // hermes_email_accounts.emergency_pause_active when the last active
  // account_pause row is dropped, so no separate UPDATE is needed here.
  const handleResumeAccount = async (provider: EmailProvider) => {
    if (blockIfEmulating('Resuming an email account')) return;
    setResumingProviderId(provider.id);
    try {
      const ok = await deactivateAccountPauseControls(provider.id);
      if (!ok) {
        toast({
          title: "Account not fully resumed",
          description:
            "Some safety controls couldn't be lifted. Try again or contact ops.",
          variant: "destructive",
        });
      } else {
        toast({
          description: `Resumed ${provider.email}. Sending will continue on the normal schedule.`,
        });
      }
      await fetchProviders();
    } catch (error) {
      console.error('Error resuming account:', error);
      captureError(error as Error, { feature: 'sender-resume' });
      toast({
        title: "Error",
        description: "Failed to resume account",
        variant: "destructive",
      });
    } finally {
      setResumingProviderId(null);
    }
  };

  if (loading) {
    return <div>Loading email providers...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Email Senders</h2>
        <p className="text-muted-foreground">
          Connect your email accounts to send emails directly from the CRM
        </p>
      </div>

      <div className="grid gap-6">
        <div className="rounded-lg border p-4">
          <p className="text-sm font-medium">Not sure which? Enter your email</p>
          <p className="mb-3 text-sm text-muted-foreground">
            We check who handles your email and highlight the button to use.
          </p>
          <EmailProviderCheck onResult={(d) => setSuggested(d?.suggestedProvider ?? null)} />
        </div>

        {/* Gmail Connection */}
        <div
          className={cn(
            "flex items-center justify-between p-4 border rounded-lg",
            suggested === "google" && "ring-2 ring-primary",
          )}
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 flex items-center justify-center bg-red-100 rounded-lg">
              <Mail className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h3 className="font-medium">Gmail</h3>
              <p className="text-sm text-muted-foreground">
                Connect multiple Gmail accounts
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => connectGmail()}
              disabled={isEmulating}
              title={isEmulating ? 'Disabled while emulating user' : undefined}
            >
              Connect Gmail
            </Button>
          </div>
        </div>

        {/* Outlook Connection */}
        <div
          className={cn(
            "flex items-center justify-between p-4 border rounded-lg",
            suggested === "microsoft" && "ring-2 ring-primary",
          )}
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 flex items-center justify-center bg-blue-100 rounded-lg">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium">Outlook</h3>
              <p className="text-sm text-muted-foreground">
                Connect multiple Outlook accounts
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => connectOutlook()}
              disabled={isEmulating}
              title={isEmulating ? 'Disabled while emulating user' : undefined}
            >
              Connect Outlook
            </Button>
          </div>
        </div>

        {/* Connected Accounts */}
        {providers.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Connected Accounts</h3>
            <div className="space-y-2">
              {providers.map((provider) => (
                <div
                  key={provider.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card"
                >
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "h-8 w-8 flex items-center justify-center rounded-lg",
                      provider.type === 'gmail' ? 'bg-red-100' : 'bg-blue-100'
                    )}>
                      <Mail className={cn(
                        "h-4 w-4",
                        provider.type === 'gmail' ? 'text-red-600' : 'text-blue-600'
                      )} />
                    </div>
                    <div>
                      <p className="font-medium">{provider.email}</p>
                      <p className="text-sm text-muted-foreground capitalize">{provider.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/*
                      Token expired (with or without an emergency pause) → Reconnect
                      forces a fresh OAuth round-trip. If the provider is also
                      emergency-paused, the connect helpers lift the safety
                      controls BEFORE redirect so the account is unblocked when
                      the user comes back.
                    */}
                    {needsReconnect.has(provider.id) && (
                      <Button
                        variant="outline"
                        onClick={() =>
                          provider.type === 'gmail'
                            ? connectGmail({ providerId: provider.id })
                            : connectOutlook({
                                forceFresh: true,
                                providerId: provider.id,
                              })
                        }
                        className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                      >
                        <RefreshCcw className="h-4 w-4 mr-2" />
                        Reconnect
                      </Button>
                    )}
                    {needsReconnect.has(provider.id) && (provider as any).auth_flow === 'spa' && provider.type === 'outlook' && (
                      <span className="text-xs text-yellow-600">Uses an old login that expires daily</span>
                    )}
                    {/*
                      Emergency-paused but token still valid → no OAuth needed,
                      just clear the safety controls. Distinct affordance so
                      users aren't forced through a re-auth they don't need.

                      Orphan-flag case: hermes_email_accounts.emergency_pause_active
                      is true but there are zero active account_pause control rows
                      to deactivate (the flag drifted from the safety-controls
                      table — can happen if controls were SQL-cleared manually or
                      by a partial-state failure on a previous token refresh).
                      The edge function has no clear-orphan action yet, so the
                      Resume click would do nothing visible. Disable the button
                      and surface the state instead of pretending to fix it.
                    */}
                    {!needsReconnect.has(provider.id) &&
                      emergencyPaused.has(provider.id) &&
                      (() => {
                        const isOrphan =
                          (accountPauseControls.get(provider.id)?.length ?? 0) === 0;
                        return (
                        <Button
                          variant="outline"
                          onClick={() => handleResumeAccount(provider)}
                          disabled={
                            resumingProviderId === provider.id ||
                            isEmulating ||
                            isOrphan
                          }
                          className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                          title={
                            isEmulating
                              ? 'Disabled while emulating user'
                              : isOrphan
                              ? 'Account is flagged as paused but no active safety control rows remain. Contact ops to clear the flag.'
                              : 'Lift the emergency pause on this account'
                          }
                        >
                          {resumingProviderId === provider.id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Resuming...
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4 mr-2" />
                              Resume account
                            </>
                          )}
                        </Button>
                      );
                      })()}
                    <Button
                      variant="outline"
                      onClick={() => setSelectedProvider(provider)}
                    >
                      Settings
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDisconnectClick(provider)}
                      disabled={checkingProviderId === provider.id}
                    >
                      {checkingProviderId === provider.id ? 'Checking...' : 'Disconnect'}
                    </Button>
                  </div>
                </div>
              ))}

              {/*
                One panel per DOMAIN, not per sender (#819 F5). Several accounts
                normally share a domain -- 8 accounts resolve to 2 domains here --
                so rendering inside providers.map showed 8 panels displaying 2
                distinct results, with 8 Re-check buttons and 8 copies of the
                privacy line. The hook already fetches once per domain; this
                makes the tree match.
              */}
              {[...new Set(
                providers
                  .map((p) => p.email?.split('@')[1]?.toLowerCase())
                  .filter((d): d is string => Boolean(d)),
              )].map((domain) => (
                <SenderDomainHealthPanel
                  key={domain}
                  domain={domain}
                  health={domainHealth[domain]}
                  loading={healthLoading}
                  refreshing={healthRefreshing === domain}
                  error={healthError}
                  onRecheck={recheckDomain}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border p-4 bg-muted/50">
        <div className="flex gap-2">
          <AlertCircle className="h-5 w-5 text-blue-500" />
          <div className="text-sm">
            <p className="font-medium">Important Note</p>
            <p className="text-muted-foreground">
              Connected email accounts are personal to your account. Only you can send from your connected accounts. Teammates may see your email domain in the Email setup check.
            </p>
          </div>
        </div>
      </div>

      {selectedProvider && (
        <SenderSettingsDialog
          open={!!selectedProvider}
          onOpenChange={(open) => !open && setSelectedProvider(null)}
          provider={selectedProvider}
          onSuccess={fetchProviders}
        />
      )}

      {pendingDisconnect && (
        <SenderDisconnectConfirmDialog
          open={!!pendingDisconnect}
          onOpenChange={(open) => {
            // Block close-via-overlay/Cancel while the disconnect is mid-flight.
            if (!open && !isDisconnecting) setPendingDisconnect(null);
          }}
          senderEmail={pendingDisconnect.provider.email}
          affectedCampaigns={pendingDisconnect.affectedCampaigns}
          isProcessing={isDisconnecting}
          onConfirm={() =>
            performDisconnect(
              pendingDisconnect.provider,
              pendingDisconnect.affectedCampaigns
            )
          }
        />
      )}
    </div>
  );
}
